import { HttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import {
  ApolloClient,
  InMemoryCache,
} from '@apollo/experimental-nextjs-app-support';
import { GraphQLError } from 'graphql';
import { jwtDecode } from 'jwt-decode';

import { getOry } from '@/utils/getOry';
import { logger } from '@/utils/logger';

// Function to parse JWT token and extract user ID
const getOryIdFromToken = (token: string | null): string | null => {
  if (!token) return null;

  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.session.identity.id || null;
  } catch (error) {
    logger.error('Error parsing JWT token:', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    return null;
  }
};

const isTokenExpiringSoon = (jwt: string, bufferSeconds = 30): boolean => {
  try {
    const decoded = jwtDecode<{ exp: number }>(jwt);
    const now = Math.floor(Date.now() / 1000);
    return decoded.exp - now < bufferSeconds;
  } catch {
    return true; // treat bad tokens as expired
  }
};

// Singleton Apollo client instance
let apolloClient: ApolloClient<InMemoryCache> | null = null;
let token: string | null = null;

// Create the Apollo client
const createApolloClient = (useServerUrl = false) => {
  // Prioritize NEXT_PUBLIC_GRAPHQL_API_URL and fall back to SERVER_GRAPHQL_API_URL
  const graphqlApiUrl = useServerUrl
    ? process.env.SERVER_GRAPHQL_API_URL
    : process.env.NEXT_PUBLIC_GRAPHQL_API_URL ||
      process.env.SERVER_GRAPHQL_API_URL;

  const httpLink = new HttpLink({
    // this needs to be an absolute url, as relative urls cannot be used in SSR
    uri: graphqlApiUrl,
    // you can disable result caching here if you want to
    // (this does not work if you are rendering your page with `export const dynamic = "force-static"`)
    fetchOptions: { cache: 'no-store' },
  });

  const retryLink = new RetryLink({
    attempts: (count, operation, error) => {
      logger.info(`Retry attempt #${count} for ${operation.operationName}`);
      const shouldRetry =
        !!error &&
        (error.networkError ||
          error.graphQLErrors?.some(
            (e: GraphQLError) => e.extensions?.code === 'INTERNAL_SERVER_ERROR',
          ));
      logger.debug('Apollo retryLink.', {
        shouldRetry,
        count,
        operation,
        error,
      });
      return shouldRetry && count <= 3;
    },
    delay: {
      initial: 300,
      max: 5000,
      jitter: true,
    },
  });

  const authLink = setContext(async (_, { headers }) => {
    if (typeof window !== 'undefined') {
      // Get token from localStorage if not already in memory
      if (!token) {
        token = localStorage.getItem('apollo-client-auth-jwt');
      }

      // Check expiry and refresh if needed
      if (token && isTokenExpiringSoon(token)) {
        try {
          await setToken(true);
        } catch (err) {
          logger.error(`Failed to refresh token from Ory: ${err}`);
          token = null;
          localStorage.removeItem('apollo-client-auth-jwt');
        }
      }
    }

    const oryId = getOryIdFromToken(token);

    return {
      headers: {
        ...headers,
        ...(token && { Authorization: `Bearer ${token}` }),
        'X-Hasura-Role': token
          ? process.env.NEXT_PUBLIC_HASURA_USER_ROLE
          : process.env.NEXT_PUBLIC_HASURA_PUBLIC_ROLE,
        ...(oryId && { 'X-Hasura-User-Id': oryId }),
      },
    };
  });

  return new ApolloClient({
    cache: new InMemoryCache(),
    link: retryLink.concat(authLink).concat(httpLink),
  });
};

const setToken = async (sessionIsActive: boolean) => {
  const tokenizeAs = process.env.NEXT_PUBLIC_ORY_JWT_TEMPLATE;
  if (!tokenizeAs) {
    logger.error('NEXT_PUBLIC_ORY_JWT_TEMPLATE not defined');
    return;
  }

  logger.debug(tokenizeAs);

  if (sessionIsActive) {
    // If we have a session, extract the JWT token
    const ory = getOry();
    const oryResult = await ory.toSession({ tokenizeAs });
    const currentToken = oryResult.tokenized;

    if (currentToken) {
      token = currentToken;
      localStorage.setItem('apollo-client-auth-jwt', currentToken);
      apolloClient = createApolloClient();
    } else {
      throw new Error('Ory session not tokenized properly.');
    }
  } else {
    token = null;
    apolloClient = createApolloClient();
  }
};

// Get or create the Apollo client instance (singleton pattern)
const getClient = (useServerUrl = false) => {
  if (!apolloClient) {
    apolloClient = createApolloClient(useServerUrl);
  }
  return apolloClient;
};

export { getClient, getOryIdFromToken, setToken };
